import { LocalTime } from 'js-joda';
import { createSelector } from 'reselect';
import _ from 'lodash';

import * as TableBasedTemplates from 'types/tableBasedTemplates';
import { FAKE_BUSINESS_DAY, businessTime } from 'utils/timeUtils';
import { NonemptyClosedNumericRange } from 'utils/range/ranges';
import { dashboard } from 'types';
import { getRange } from 'utils/tableBasedTemplatesUtils';
import {
  globalTurnTimesByTicketTypeSelector,
  serviceDayTurnTimesByTicketTypeSelector,
} from 'selectors/routes/turn-times/turnTimeEntitiesByTicketTypeSelector';
import ClosedIntegerRangeMap from 'utils/range/ClosedIntegerRangeMap';

export const globalTurnTimesBySizeSelector = createSelector(
  globalTurnTimesByTicketTypeSelector,
  computeNewStyleRanges
);

export const serviceDayTurnTimesBySizeSelector = createSelector(
  serviceDayTurnTimesByTicketTypeSelector,
  computeNewStyleRanges
);

function computeNewStyleRanges(turnTimesByTicketType: {
  [ticketTypeId: number]: dashboard.TurnTime[];
}): TableBasedTemplates.TurnTimes {
  const defaultTurnTimesByTicketType = computeNewStyleRangeForExperience(turnTimesByTicketType[-1]);
  const ticketTypeTurnTimesByTicketType = Object.fromEntries(
    Object.entries(turnTimesByTicketType).filter(([ticketTypeId]) => Number(ticketTypeId) > 0)
  );
  const ticketTypeTurnTimesWithTicketTypes = _.mapValues(
    ticketTypeTurnTimesByTicketType,
    (turnTimes) => computeNewStyleRangeForExperience(turnTimes, defaultTurnTimesByTicketType)
  );
  return { [-1]: defaultTurnTimesByTicketType, ...ticketTypeTurnTimesWithTicketTypes };
}
function computeNewStyleRangeForExperience(
  turnTimes: dashboard.TurnTime[],
  defaultTurnTimes?: TableBasedTemplates.SingleExperienceTurnTimes
): TableBasedTemplates.SingleExperienceTurnTimes {
  const turnTimesByPartySizeRange = groupByTurnTime(turnTimes);
  return new ClosedIntegerRangeMap(
    _.map(turnTimesByPartySizeRange, (grouping) => {
      return {
        range: grouping.range,
        value: computeStartTimeToTurnTimeMap(grouping.values, defaultTurnTimes),
      };
    })
  );
}

interface Grouping {
  range: NonemptyClosedNumericRange;
  values: dashboard.TurnTime[];
}

function groupByTurnTime(turnTimes: dashboard.TurnTime[]): Grouping[] {
  const groupings: Grouping[] = [];
  _.forEach(turnTimes, (turnTime) => {
    const turnTimeRange = getRange(turnTime);
    let existingGrouping = _.find(groupings, (grouping) => grouping.range.equals(turnTimeRange));
    if (!existingGrouping) {
      existingGrouping = {
        range: turnTimeRange,
        values: [],
      };
      groupings.push(existingGrouping);
    }
    existingGrouping.values.push(turnTime);
  });
  return groupings;
}

/**
 * Computes a fleshed-out turn time map for the given raw list of turn times,
 * which are assumed to all pertain to the same ticket type and party size
 * @param turnTimes a nonempty list of turn times returned by the server
 */
function computeStartTimeToTurnTimeMap(
  turnTimes: dashboard.TurnTime[],
  defaultTurnTimes?: TableBasedTemplates.SingleExperienceTurnTimes
): TableBasedTemplates.SinglePartySizeTurnTimes {
  const sortedTurnTimes = _.sortBy(turnTimes, (turnTime) =>
    businessTime(LocalTime.parse(turnTime.startingAtTime)).toString()
  );

  const startTimeToTurnTime: TableBasedTemplates.SinglePartySizeTurnTimes = {};
  // it's okay to index into turnTimes here because sortedByTime will
  // never map to empty lists
  let currentTurnTime = sortedTurnTimes[0];
  // Start on index 1; we always want the first element in the list to
  // start at 4AM no matter what it's configured for
  let nextTurnTimeIdx = 1;
  for (
    let time = businessTime(LocalTime.of(4, 0));
    time.isBefore(FAKE_BUSINESS_DAY.plusDays(1).atTime(LocalTime.of(4, 0)));
    time = time.plusMinutes(15)
  ) {
    const nextTurnTime = turnTimes[nextTurnTimeIdx];
    let useDefaultTurnTime = false;
    if (
      turnTimes.length > nextTurnTimeIdx &&
      !businessTime(LocalTime.parse(nextTurnTime.startingAtTime)).isAfter(time)
    ) {
      currentTurnTime = nextTurnTime;
      nextTurnTimeIdx++;
    }

    if (
      currentTurnTime.ticketTypeId > 0 &&
      currentTurnTime.serviceEndingAtTime &&
      time
        .toLocalTime()
        .isAfterConsideringBusinessDay(LocalTime.parse(currentTurnTime.serviceEndingAtTime))
    ) {
      useDefaultTurnTime = true;
    }

    const defaultTurnTime = defaultTurnTimes?.get(currentTurnTime.minPartySize)?.[
      time.toLocalTime().toString()
    ];
    startTimeToTurnTime[time.toLocalTime().toString()] =
      useDefaultTurnTime && defaultTurnTime ? defaultTurnTime : currentTurnTime;
  }
  return startTimeToTurnTime;
}
